"use client";

import { createPortal } from "react-dom";
import { Box, Stack, styled } from "@mui/material";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import FadeIn from "@components/shared/loading/FadeIn";
import useCandidateDetails from "@lib/candidates/useCandidateDetails";
import useDownloadOriginalCv from "@lib/candidates/useDownloadOriginalCv";
import BackButton from "@components/shared/BackButton";
import useContextualBack from "@lib/navigation/useContextualBack";
import PageTitle from "@components/shared/Typography/PageTitle";
import PageSkeleton from "@components/shared/loading/PageSkeleton";
import useMutateCandidate from "@lib/candidates/useMutateCandidate";
import useMutateCandidateImage from "@lib/candidates/useMutateCandidateImage";
import useMutateCandidateAvatarFromUrl from "@lib/candidates/useMutateCandidateAvatarFromUrl";
import PersonalDetails from "./parts/PersonalDetails";
import Tags from "./parts/Tags";
import Portfolio from "./parts/Portfolio";
import Summary from "./parts/Summary";
import Skills from "./parts/Skills";
import Roles from "./parts/Roles";
import Projects from "./parts/Projects";
import Education from "./parts/Education";
import Culture from "./parts/Culture";
import CandidateHeaderDetails from "./parts/CandidateHeaderDetails";
import CandidateActions from "./parts/CandidateActions";
import CandidateTabs from "./parts/CandidateTabs";
import useIndividualImportingStatus from "../hooks/useIndividualImportingStatus";

const Container = styled(Box)(({ theme }) => ({
  padding: "40px 46px 40px 46px",
  [theme.breakpoints.down("sm")]: {
    padding: "20px",
  },
}));

const slugifyName = (value) =>
  (value || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function Candidate({
  candidateId,
  hideBackButton = false,
  hideActions = false,
  actionsContainer = null,
}) {
  const router = useRouter();
  const { label: backLabel, goBack } = useContextualBack("/candidates", "Candidates");
  const { candidate, isLoading, invalidate } = useCandidateDetails(
    candidateId,
    true
  );
  const candidateMutation = useMutateCandidate();
  const { mutation: imageMutation } = useMutateCandidateImage();
  const { mutation: avatarFromUrlMutation } = useMutateCandidateAvatarFromUrl();
  const downloadOriginalCv = useDownloadOriginalCv(candidateId, candidate?.name);

  const {
    isImportingCulture,
    isImportingRoles,
    isImportingProjects,
    isImportingEducation,
    isImportingSkills,
    isGeneratingSummary,
  } = useIndividualImportingStatus(candidate?.id, invalidate);

  const handleFloat = () => {
    router.push(`/floats/create?candidates[]=${candidate.id}`);
  };

  const handleSubmit = () => {
    router.push(`/submissions/create?candidates[]=${candidate.id}`);
  };

  const handleUpdate = (candidate) => {
    const toastId = toast.loading("Saving...");
    candidateMutation.mutate(candidate, {
      onSuccess: () => {
        toast.dismiss(toastId);
      },
      onError: () => {
        toast.dismiss(toastId);
        toast.error("Failed to save");
      },
    });
  };

  const handleImageUpdate = (image) => {
    const toastId = toast.loading("Saving...");
    imageMutation.mutate(
      { candidate, image },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleAvatarFromLibrary = ({ url, source }) => {
    // "initials" carries no url — clearing photo_url is what makes the client
    // fall back to initials. The API has no matching source, so it never runs.
    if (source === "initials" || !url) {
      handleUpdate({ ...candidate, photo_url: null });
      return;
    }
    const toastId = toast.loading("Saving...");
    avatarFromUrlMutation.mutate(
      { candidate, url, source },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleExportPDF = async () => {
    const toastId = toast.loading("Generating PDF…");
    try {
      const res = await fetch(`/api/candidates/${candidateId}/resume/pdf`, {
        method: "GET",
      });
      if (!res.ok) {
        throw new Error(`PDF request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cv-${slugifyName(candidate?.name) || candidateId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded", { id: toastId });
    } catch (err) {
      console.error("Failed to export PDF:", err);
      toast.error("Failed to export PDF", { id: toastId });
    }
  };

  const handleExportOriginalPDF = async () => {
    const toastId = toast.loading("Generating branded original PDF…");
    try {
      const res = await fetch(
        `/api/candidates/${candidateId}/resume/original-pdf`,
        {
          method: "GET",
        }
      );
      if (!res.ok) {
        throw new Error(`PDF request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cv-${slugifyName(candidate?.name) || candidateId}-original.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Branded original PDF downloaded", { id: toastId });
    } catch (err) {
      console.error("Failed to export branded original PDF:", err);
      toast.error("Failed to export branded original PDF", { id: toastId });
    }
  };

  const profileContent = candidate && (
    <>
      <PersonalDetails candidate={candidate} onUpdate={handleUpdate} />
      <Tags candidate={candidate} />
      <Portfolio candidate={candidate} />
      <Summary candidate={candidate} onUpdate={handleUpdate} />
      <Culture
        candidate={candidate}
        onUpdate={handleUpdate}
        isImporting={isImportingCulture}
      />
      <Skills
        candidate={candidate}
        onUpdate={handleUpdate}
        isImporting={isImportingSkills}
      />
      <Roles
        candidate={candidate}
        onUpdate={handleUpdate}
        isImporting={isImportingRoles}
      />
      <Projects
        candidate={candidate}
        onUpdate={handleUpdate}
        isImporting={isImportingProjects}
      />
      <Education
        candidate={candidate}
        onUpdate={handleUpdate}
        isImporting={isImportingEducation}
      />
    </>
  );

  return (
    <Box>
      <Container>
        {!hideBackButton && <BackButton title={backLabel} onClick={goBack} />}
        <FadeIn isLoading={isLoading}>
          <Box>
            {!candidate && (
              <Box sx={{ p: 4 }}>
                <PageSkeleton />
              </Box>
            )}
            {candidate && (
              <Box>
              <Stack
                sx={{ mt: 0, mb: 4 }}
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={3}
              >
                <Box>
                  <PageTitle>{candidate.name}</PageTitle>
                  <CandidateHeaderDetails candidate={candidate} />
                </Box>
                {!hideActions && !actionsContainer && (
                  <CandidateActions
                    candidate={candidate}
                    onFloat={handleFloat}
                    onSubmit={handleSubmit}
                  />
                )}
              </Stack>
              {!hideActions &&
                actionsContainer &&
                createPortal(
                  <CandidateActions
                    candidate={candidate}
                    onFloat={handleFloat}
                    onSubmit={handleSubmit}
                    showSave
                    size="small"
                  />,
                  actionsContainer
                )}
                <CandidateTabs
                  candidate={candidate}
                  candidateId={candidateId}
                  profile={profileContent}
                  onImageUpdate={handleImageUpdate}
                  onAvatarFromLibrary={handleAvatarFromLibrary}
                  onDownloadOriginal={downloadOriginalCv}
                  onDownloadFormatted={handleExportPDF}
                  onDownloadFormattedOriginal={handleExportOriginalPDF}
                  hasResume={!!candidate.has_resume}
                  hasRawResume={!!candidate.has_raw_resume_text}
                  isGeneratingSummary={isGeneratingSummary}
                />
              </Box>
            )}
          </Box>
        </FadeIn>
      </Container>
    </Box>
  );
}
